import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useLocation } from 'react-router';
import type { User } from '@/utils/types/User';
import { EventQueueManager, type StorageAdapter } from './eventQueue';
import {
  type AnalyticsContext,
  type ContextDependencies,
  gatherAnalyticsContext,
  createAnalyticsContextDependencies,
} from './contextGatherer';
import { type SegmentAnalytics, SegmentAdapter } from './segmentAdapter';
import { getEnv } from '@/shared/configs';
import type { Context, ID } from '@segment/analytics-next';

export interface AnalyticsConfig {
  writeKey?: string;
  retryAttempts?: number;
  queueSize?: number;
  // Dependency injection for testing
  segmentAdapter?: SegmentAnalytics;
  contextDependencies?: ContextDependencies;
  storage?: StorageAdapter;
  timerFn?: typeof setTimeout;
}

export interface AnalyticsState {
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
  retryCount: number;
}

export interface UseAnalyticsReturn {
  track: (eventName: string, payload?: Record<string, any>) => Promise<Context | void>;
  page: (pageName?: string, properties?: Record<string, any>) => Promise<Context | void>;
  identify: (user: User) => Promise<Context | void>;
  setAnonymousUserId: (id: string) => Promise<ID | void>;
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
  getQueuedEventCount: () => number;
}

const STORAGE_KEY = 'segment_analytics_queue';
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
const MAX_QUEUE_SIZE = 100;
const QUEUE_PERSIST_TIME_MS = 24 * 60 * 60 * 1000; // 24 hours

const mapUserToIdentifyPayload = (user: User) => ({
  name: `${user.firstName} ${user.lastName}`,
  email: user.email,
  sizeOfPortfolio: user.sizeOfPortfolio ?? undefined,
});

const defaultStorageAdapter: StorageAdapter = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

export function useAnalytics(config: AnalyticsConfig = {}): UseAnalyticsReturn {
  const {
    timerFn = setTimeout,
    storage = defaultStorageAdapter,
    writeKey = getEnv().segment_api_key,
    retryAttempts = MAX_RETRY_ATTEMPTS,
    queueSize = MAX_QUEUE_SIZE,
    segmentAdapter,
    contextDependencies = createAnalyticsContextDependencies(),
  } = config;

  const location = useLocation();
  const isFlushingEventsMutexLockRef = useRef(false);
  const analyticsRef = useRef<SegmentAnalytics | null>(segmentAdapter || null);
  const queueManagerRef = useRef<EventQueueManager | null>(null);
  const initializationPromiseRef = useRef<Promise<void> | null>(null);
  const prevPathRef = useRef<string | null>(null);

  const [state, setState] = useState<AnalyticsState>(() => ({
    isLoading: Boolean(writeKey) && !segmentAdapter,
    isReady: Boolean(segmentAdapter),
    retryCount: 0,
    error: null,
  }));

  if (!queueManagerRef.current) {
    queueManagerRef.current = new EventQueueManager({
      persistTimeMs: QUEUE_PERSIST_TIME_MS,
      maxQueueSize: queueSize,
      storageKey: STORAGE_KEY,
      storage,
    });
  }

  /**
   * =============================================================
   * Helper functions
   * =============================================================
   */
  const getQueuedEventCount = useCallback(() => {
    return queueManagerRef.current?.getEventCount() || 0;
  }, []);

  const getAnalyticsContext = useCallback((): AnalyticsContext => {
    return gatherAnalyticsContext(contextDependencies);
  }, [contextDependencies]);

  /**
   * =============================================================
   * Function to initialize analytics
   * Waits for Segment to be ready before finalizing
   * =============================================================
   */
  const initializeAnalytics = useCallback(async () => {
    if (!writeKey) {
      const error = new Error('Segment write key is missing');
      setState((prev) => ({ ...prev, isLoading: false, error }));
      return;
    }

    if (analyticsRef.current) {
      flushQueuedEvents();
      return;
    }

    if (segmentAdapter) {
      analyticsRef.current = segmentAdapter;
      setState((prev) => ({ ...prev, isReady: true, isLoading: false, retryCount: 0 }));
      flushQueuedEvents();
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const adapter = new SegmentAdapter({ writeKey });
      await adapter.ready();

      analyticsRef.current = adapter;

      setState((prev) => ({
        ...prev,
        isReady: true,
        isLoading: false,
        retryCount: 0,
      }));

      await flushQueuedEvents();
    } catch (error) {
      const analyticsError =
        error instanceof Error ? error : new Error('Analytics initialization failed');

      let nextRetry = 0;
      setState((prev) => {
        nextRetry = prev.retryCount + 1;
        return { ...prev, isLoading: false, error: analyticsError, retryCount: nextRetry };
      });

      if (nextRetry >= retryAttempts) {
        return;
      }

      timerFn(
        () => initializeAnalytics(),
        RETRY_DELAY_MS * 2 ** nextRetry, // exponential backoff
      );
    }
  }, [writeKey, retryAttempts, state.retryCount, segmentAdapter, timerFn]);

  /**
   * =============================================================
   * Function to flush queued events, supports all the basic event tracking functions
   * Prevents double flushing by using a ref as a mutex lock
   * =============================================================
   */
  const flushQueuedEvents = useCallback(async () => {
    if (isFlushingEventsMutexLockRef.current) {
      return;
    }

    if (!analyticsRef.current || !queueManagerRef.current) {
      return;
    }

    const queuedEvents = queueManagerRef.current.getEvents();
    if (queuedEvents.length === 0) {
      return;
    }

    isFlushingEventsMutexLockRef.current = true;
    const analytics = analyticsRef.current;
    const successfulEventIds: string[] = [];
    for (const event of queuedEvents) {
      try {
        const contextData = getAnalyticsContext();

        switch (event.type) {
          case 'track':
            if (!event.eventName) {
              break;
            }
            await analytics.track(event.eventName, { ...event.payload, ...contextData });
            break;
          case 'page':
            await analytics.page(event.eventName || '', { ...event.payload, ...contextData });
            break;
          case 'identify':
            if (!event.userId) {
              break;
            }
            await analytics.identify(event.userId, event.payload);
            break;
          case 'setAnonymousId':
            if (!event.anonymousId) {
              break;
            }
            await analytics.setAnonymousId(event.anonymousId);
            break;
        }

        successfulEventIds.push(event.id);
      } catch (_) {
        isFlushingEventsMutexLockRef.current = false;
      }
    }

    if (successfulEventIds.length > 0) {
      queueManagerRef.current.removeEvents(successfulEventIds);
    }

    isFlushingEventsMutexLockRef.current = false;
  }, [getAnalyticsContext]);

  /**
   * =============================================================
   * Basic event tracking functions supported by the queue manager
   * =============================================================
   */
  const track = useCallback(
    async (eventName: string, payload: Record<string, any> = {}): Promise<Context | void> => {
      if (!analyticsRef.current || !state.isReady) {
        queueManagerRef.current?.addEvent({ type: 'track', eventName, payload });
        return;
      }

      try {
        const contextData = getAnalyticsContext();
        return await analyticsRef.current.track(eventName, { ...payload, ...contextData });
      } catch (_) {
        return queueManagerRef.current?.addEvent({ type: 'track', eventName, payload });
      }
    },
    [state.isReady, getAnalyticsContext],
  );

  const page = useCallback(
    async (pageName?: string, properties: Record<string, any> = {}): Promise<Context | void> => {
      const pathname = pageName || location.pathname;

      if (!analyticsRef.current || !state.isReady) {
        queueManagerRef.current?.addEvent({
          eventName: pathname,
          payload: properties,
          type: 'page',
        });
        return;
      }

      try {
        const contextData = getAnalyticsContext();

        let additionalProperties = {};
        if (location.hash) {
          additionalProperties = {
            hash: location.hash,
            pathname: location.pathname,
            url: window.location.href,
          };
        }

        return await analyticsRef.current.page(pathname, {
          ...properties,
          ...contextData,
          ...additionalProperties,
          referrer: prevPathRef.current ?? document.referrer ?? '',
        });
      } catch (_) {
        queueManagerRef.current?.addEvent({
          eventName: pathname,
          payload: properties,
          type: 'page',
        });
      }
    },
    [location, state.isReady, getAnalyticsContext],
  );

  const identify = useCallback(
    async (user: User): Promise<Context | void> => {
      const identifyPayload = mapUserToIdentifyPayload(user);

      if (!analyticsRef.current || !state.isReady) {
        queueManagerRef.current?.addEvent({
          payload: identifyPayload,
          type: 'identify',
          userId: user.id,
        });
        return;
      }

      try {
        return await analyticsRef.current.identify(user.id!, identifyPayload);
      } catch (_) {
        return queueManagerRef.current?.addEvent({
          payload: identifyPayload,
          type: 'identify',
          userId: user.id,
        });
      }
    },
    [state.isReady],
  );

  const setAnonymousUserId = useCallback(
    async (id: string): Promise<ID | void> => {
      if (!analyticsRef.current || !state.isReady) {
        queueManagerRef.current?.addEvent({ type: 'setAnonymousId', anonymousId: id });
        return;
      }

      try {
        return await analyticsRef.current.setAnonymousId(id);
      } catch (_) {
        return queueManagerRef.current?.addEvent({ type: 'setAnonymousId', anonymousId: id });
      }
    },
    [state.isReady],
  );

  /**
   * =============================================================
   * UseEffects to handle the lifecycle of the analytics instance,
   * online and offline events, and flushing queued events
   * =============================================================
   */
  useEffect(() => {
    if (segmentAdapter) {
      if (analyticsRef.current === segmentAdapter) {
        return;
      }

      initializationPromiseRef.current = initializeAnalytics();
      return;
    }

    if (initializationPromiseRef.current) {
      return;
    }

    initializationPromiseRef.current = initializeAnalytics();
  }, [initializeAnalytics, writeKey, segmentAdapter]);

  useEffect(() => {
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const handleOnline = async () => {
      if (state.isReady) {
        await flushQueuedEvents();
        return;
      }

      if (state.error && state.retryCount < retryAttempts) {
        setState((prev) => ({ ...prev, retryCount: 0 }));
        await initializeAnalytics();
      }
    };

    const handleOffline = () => {
      if (!state.isReady) {
        return;
      }

      analyticsRef.current = null;
      setState((prev) => ({
        ...prev,
        isReady: false,
        isLoading: false,
        error: new Error('Connection lost'),
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [
    state.isReady,
    state.error,
    state.retryCount,
    retryAttempts,
    flushQueuedEvents,
    initializeAnalytics,
  ]);

  const returnValue = useMemo(
    () => ({
      track,
      page,
      identify,
      setAnonymousUserId,
      isReady: state.isReady,
      isLoading: state.isLoading,
      error: state.error,
      getQueuedEventCount,
    }),
    [
      track,
      page,
      identify,
      setAnonymousUserId,
      state.isReady,
      state.isLoading,
      state.error,
      getQueuedEventCount,
    ],
  );

  return returnValue;
}
